// Pure helpers for the epic report page: the tabs, the period keys the server expects, and the CSV
// of the open tab. Kept apart from the page so they can be tested without rendering it.

import { toCsv } from './download';
import * as fmt from '../lib/format';

// The epic roster is read two ways and each way adds up to the total: by status and by category.
export type StatusTabId = 'todo' | 'in_progress' | 'completed' | 'on_hold';
export type CategoryTabId = 'devops' | 'automation' | 'adhoc' | 'rest';
export type EpicTabId = StatusTabId | CategoryTabId;

export interface EpicTabDef<T extends EpicTabId> {
  id: T;
  label: string;
}

export const STATUS_TABS: EpicTabDef<StatusTabId>[] = [
  { id: 'todo', label: 'Planned/To Do' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'on_hold', label: 'IceBox/On Hold' },
];

export const CATEGORY_TABS: EpicTabDef<CategoryTabId>[] = [
  { id: 'devops', label: 'DevOps' },
  { id: 'automation', label: 'Automation' },
  { id: 'adhoc', label: 'Ad Hoc' },
  { id: 'rest', label: 'Rest of Task' },
];

export const ALL_TABS: EpicTabDef<EpicTabId>[] = [...STATUS_TABS, ...CATEGORY_TABS];

export function tabLabel(id: EpicTabId): string {
  return ALL_TABS.find((t) => t.id === id)?.label ?? id;
}

// ─── What the server returns ──────────────────────────────────────

export interface EpicStatusStep {
  created_at: string;
  status: string;
}

export interface EpicReportRow {
  issue_id: string;
  issue_key: string;
  issue_name: string | null;
  project_key: string;
  project_name: string | null;
  priority: string | null;
  /** Status at the end of the period (right now for an open one). */
  status: string;
  status_group: 'todo' | 'in_progress' | 'on_hold' | 'done';
  /** First paragraph of the description; empty when there is none. */
  description: string;
  /** own: a real description. blank: none. repeated: the same text as several other epics of the period. */
  description_kind: 'own' | 'blank' | 'repeated';
  /** What is in the epic, for the rows whose description says nothing; null for the others. */
  inside: { tickets: number; open: number; hours: number; latest: { key: string; name: string } | null } | null;
  /** `inside` as one templated line: "64 tickets, 5 open · 12 h logged in October 2026 · latest: SRL-2901 …". */
  inside_line: string | null;
  last_change_at: string;
  /** Oldest first: the last step is the status at the end of the period. */
  status_timeline: EpicStatusStep[];
}

export interface EpicReportCounts {
  total: number;
  status: Record<StatusTabId, number>;
  category: Record<CategoryTabId, number>;
}

export interface EpicReportMeta {
  period: string;
  label: string;
  as_at: string;
  /** "31 Oct 2026" for a finished period, "today" for an open one. */
  as_at_label: string;
  is_current: boolean;
}

/** The count behind a tab's badge. */
export function countFor(counts: EpicReportCounts, id: EpicTabId): number {
  return STATUS_TABS.some((t) => t.id === id) ? counts.status[id as StatusTabId] : counts.category[id as CategoryTabId];
}

// ─── Period keys ──────────────────────────────────────────────────

/** "2026-10" stays as it is. */
export const monthPeriodKey = (monthValue: string): string => monthValue;

/** The pickers hold a quarter as "2026-4"; the server reads "2026-Q4". */
export function quarterPeriodKey(quarterValue: string): string {
  const [year, quarter] = quarterValue.split('-');
  return `${year}-Q${quarter}`;
}

/** "2026" stays as it is. */
export const yearPeriodKey = (yearValue: string): string => yearValue;

// ─── CSV ────────────────────────────────────────────────────────

export interface CsvEpic {
  row: EpicReportRow;
  /** The title as the page shows it ("Project | Epic name"). */
  task: string;
}

export const EPIC_CSV_HEADER = ['Issue Key', 'Task', 'Project', 'Status', 'Priority', 'Last change', 'Description', "What's inside", 'Status history (oldest first)'];

export function epicsToCsv(epics: CsvEpic[]): string {
  return toCsv(
    EPIC_CSV_HEADER,
    epics.map(({ row, task }) => [
      row.issue_key,
      task,
      row.project_name ? `${row.project_name} (${row.project_key})` : row.project_key,
      row.status,
      row.priority ?? '',
      fmt.date(row.last_change_at),
      // A description that is blank or the same on several epics is left out; what is inside takes its place.
      row.description_kind === 'own' ? row.description : '',
      row.inside_line ?? '',
      row.status_timeline.map((s) => `${fmt.date(s.created_at)} - ${s.status}`).join('; '),
    ])
  );
}

// ─── Files ─────────────────────────────────────────────────────

/** "Epic-report-2026-10.csv", "Epic-report-2026-10-in-progress.csv". */
export function reportFilename(periodKey: string, extension: 'csv' | 'xlsx', tab?: EpicTabId): string {
  const suffix = tab ? `-${tabLabel(tab).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` : '';
  return `Epic-report-${periodKey}${suffix}.${extension}`;
}
